/**
 * Badge Component
 * =================
 * Reusable badge/tag component for status indicators and labels.
 */

import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

export interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'sm' | 'md' | 'lg';
  rounded?: 'default' | 'full';
  dot?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  className?: string;
}

// =============================================================================
// STYLES
// =============================================================================

const variantStyles: Record<string, string> = {
  default: 'bg-gray-100 text-gray-800',
  primary: 'bg-primary-100 text-primary-800',
  secondary: 'bg-gray-100 text-gray-600',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
};

const dotStyles: Record<string, string> = {
  default: 'bg-gray-400',
  primary: 'bg-primary-500',
  secondary: 'bg-gray-400',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  danger: 'bg-red-500',
  info: 'bg-blue-500',
};

const sizeStyles: Record<string, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-sm',
  lg: 'px-3 py-1 text-sm',
};

// =============================================================================
// COMPONENT
// =============================================================================

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  rounded = 'default',
  dot = false,
  removable = false,
  onRemove,
  className,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium',
        rounded === 'full' ? 'rounded-full' : 'rounded-md',
        variantStyles[variant],
        sizeStyles[size],
        className
      )}
    >
      {dot && (
        <span
          className={cn(
            'mr-1.5 h-1.5 w-1.5 rounded-full',
            dotStyles[variant]
          )}
        />
      )}
      {children}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            'ml-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
            'hover:bg-black/10 focus:outline-none'
          )}
          aria-label="Remove"
        >
          <svg
            className="h-3 w-3"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
}

// =============================================================================
// STATUS BADGE
// =============================================================================

export interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusVariants: Record<string, BadgeProps['variant']> = {
  // Chart statuses
  draft: 'secondary',
  finalized: 'success',
  amended: 'info',
  
  // Patient statuses
  active: 'success',
  inactive: 'secondary',
  
  // Risk levels
  low: 'success',
  moderate: 'warning',
  high: 'danger',
  
  // Generic
  pending: 'warning',
  completed: 'success',
  cancelled: 'danger',
  in_progress: 'info',
};

export function StatusBadge({ status, size = 'sm', className }: StatusBadgeProps) {
  const normalizedStatus = status.toLowerCase().replace(/\s+/g, '_');
  const variant = statusVariants[normalizedStatus] || 'default';
  
  // Format display text
  const displayText = status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Badge variant={variant} size={size} dot className={className}>
      {displayText}
    </Badge>
  );
}

// =============================================================================
// RISK LEVEL BADGE
// =============================================================================

export interface RiskBadgeProps {
  level: 'low' | 'moderate' | 'high';
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function RiskBadge({
  level,
  showIcon = false,
  size = 'sm',
  className,
}: RiskBadgeProps) {
  const variants: Record<string, BadgeProps['variant']> = {
    low: 'success',
    moderate: 'warning',
    high: 'danger',
  };

  const labels: Record<string, string> = {
    low: 'Low Risk',
    moderate: 'Moderate Risk',
    high: 'High Risk',
  };

  return (
    <Badge variant={variants[level]} size={size} dot={showIcon} className={className}>
      {labels[level]}
    </Badge>
  );
}

// =============================================================================
// TOOTH STATUS BADGE
// =============================================================================

export interface ToothStatusBadgeProps {
  status: 'present' | 'missing' | 'implant' | 'pontic' | 'crown' | 'extraction_planned';
  size?: 'sm' | 'md';
  className?: string;
}

export function ToothStatusBadge({
  status,
  size = 'sm',
  className,
}: ToothStatusBadgeProps) {
  const variants: Record<string, BadgeProps['variant']> = {
    present: 'default',
    missing: 'secondary',
    implant: 'info',
    pontic: 'secondary',
    crown: 'primary',
    extraction_planned: 'warning',
  };

  const labels: Record<string, string> = {
    present: 'Present',
    missing: 'Missing',
    implant: 'Implant',
    pontic: 'Pontic',
    crown: 'Crown',
    extraction_planned: 'Planned Extraction',
  };

  return (
    <Badge variant={variants[status]} size={size} className={className}>
      {labels[status]}
    </Badge>
  );
}

export default Badge;
